import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Home, Euro, Calendar, Users, Check, X, Clock, 
  Phone, Mail, Key, Copy, Search, User, Gift, UserPlus, Receipt, 
  Wallet, CreditCard, Building, Globe, MessageSquare, MessageCircle, 
  FileText, Settings, Activity, RefreshCw, AlertTriangle, List, 
  LayoutGrid, MoreVertical, Eye, Printer, Download, Filter,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ArrowUpDown, ExternalLink, DoorOpen
} from 'lucide-react';
import CommunicationTimeline from '../../components/CommunicationTimeline';
import BookingCalendar from '../../components/BookingCalendar';
import { API } from '../../lib/api';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Format currency in Italian locale
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '€0,00';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

// Format date in Italian style
const formatDateItalian = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  } catch {
    return dateStr;
  }
};

// Format short date (DD/MM)
const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  } catch {
    return dateStr;
  }
};

// Get booking source from booking data
const getBookingSource = (booking) => {
  const note = (booking.note || '').toLowerCase();
  const source = (booking.source || '').toLowerCase();
  const channel = (booking.channel || '').toLowerCase();
  
  if (note.includes('[booking') || source.includes('booking') || channel === 'booking') return 'booking';
  if (note.includes('[airbnb') || source.includes('airbnb') || channel === 'airbnb') return 'airbnb';
  if (note.includes('[whatsapp')) return 'whatsapp';
  if (note.includes('[telefono') || note.includes('[phone')) return 'telefono';
  return 'direct';
};

// Get channel badge component
const ChannelBadge = ({ booking }) => {
  const source = getBookingSource(booking);
  
  const badges = {
    booking: (
      <span className="inline-flex items-center gap-1 bg-[#003580] text-white px-2 py-0.5 rounded-full text-xs font-medium">
        <svg className="w-3 h-3" viewBox="0 0 512 512" fill="currentColor">
          <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256 256-114.6 256-256S397.4 0 256 0zm-47.5 352h-48v-192h80c35.3 0 64 28.7 64 64 0 22.6-11.8 42.5-29.6 53.8L307.5 352h-56l-27-56h-16v56zm32-88h32c8.8 0 16-7.2 16-16s-7.2-16-16-16h-32v32z"/>
        </svg>
        Booking
      </span>
    ),
    airbnb: (
      <span className="inline-flex items-center gap-1 bg-[#FF5A5F] text-white px-2 py-0.5 rounded-full text-xs font-medium">
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.001 18.275c-.942-1.299-1.553-2.478-1.846-3.518-.273-.97-.26-1.807.044-2.504.282-.65.753-1.147 1.352-1.45a2.78 2.78 0 011.35-.35c.482 0 .942.116 1.35.35.6.303 1.07.8 1.353 1.45.304.697.317 1.533.044 2.504-.293 1.04-.904 2.22-1.846 3.518-.466.64-.8 1.088-.9 1.225-.1-.137-.435-.585-.901-1.225z"/>
        </svg>
        Airbnb
      </span>
    ),
    whatsapp: (
      <span className="inline-flex items-center gap-1 bg-[#25D366] text-white px-2 py-0.5 rounded-full text-xs font-medium">
        <MessageCircle className="w-3 h-3" />
        WhatsApp
      </span>
    ),
    telefono: (
      <span className="inline-flex items-center gap-1 bg-gray-600 text-white px-2 py-0.5 rounded-full text-xs font-medium">
        <Phone className="w-3 h-3" />
        Telefono
      </span>
    ),
    direct: (
      <span className="inline-flex items-center gap-1 bg-emerald-600 text-white px-2 py-0.5 rounded-full text-xs font-medium">
        <Globe className="w-3 h-3" />
        Diretta
      </span>
    )
  };
  
  return badges[source] || badges.direct;
};

// Calculate tourist tax based on settings
const calculateTouristTax = (checkinDate, numAdults, numNights, tariffs, defaultRate = 3.0) => {
  if (!checkinDate || !numAdults || numAdults <= 0 || !numNights || numNights <= 0) return 0;
  
  // Try to find matching tariff for the date
  let rate = defaultRate;
  if (tariffs && tariffs.length > 0) {
    const checkin = new Date(checkinDate);
    for (const tariff of tariffs) {
      const start = new Date(tariff.start_date);
      const end = new Date(tariff.end_date);
      if (checkin >= start && checkin <= end) {
        rate = tariff.rate || defaultRate;
        break;
      }
    }
  }
  
  return numAdults * numNights * rate;
};

// Calculate net amount (after deductions) for a booking
const calculateNetAmount = (lordo, source, financialSettings, tassaSoggiorno = 0) => {
  if (!lordo || lordo <= 0) return 0;
  
  const isOTA = ['booking', 'airbnb', 'expedia'].includes(source?.toLowerCase() || '');
  const isDirect = !isOTA;
  
  // Imponibile = Lordo - Tassa Soggiorno
  const imponibile = lordo - tassaSoggiorno;
  
  if (isDirect) {
    // Dirette: NO IVA, NO commissioni OTA
    return imponibile;
  }
  
  // OTA: calcola tutte le detrazioni - IVA dalle impostazioni
  const ivaPercent = financialSettings?.iva_percent || 10;
  const otaPercent = source?.toLowerCase() === 'booking' 
    ? (financialSettings?.commissione_booking || 15) 
    : source?.toLowerCase() === 'airbnb' 
      ? (financialSettings?.commissione_airbnb || 3) 
      : 15;
  const transPercent = financialSettings?.costo_transazione || 1.5;
  const cedolarePercent = financialSettings?.cedolare_secca || 21;
  
  const iva = imponibile * ivaPercent / 100;
  const commOTA = imponibile * otaPercent / 100;
  const commTrans = imponibile * transPercent / 100;
  const preCedolare = imponibile - iva - commOTA - commTrans;
  const cedolare = preCedolare * cedolarePercent / 100;
  
  return preCedolare - cedolare;
};

// Status badge component
const StatusBadge = ({ booking }) => {
  const opStatus = booking.operational_status;
  const opLabel = booking.operational_label;
  const opColor = booking.operational_color;
  
  if (opStatus && opLabel) {
    const colorMap = {
      'green': 'bg-green-100 text-green-800 border-green-300',
      'blue': 'bg-blue-100 text-blue-800 border-blue-300',
      'orange': 'bg-orange-100 text-orange-800 border-orange-300',
      'teal': 'bg-teal-100 text-teal-800 border-teal-300',
      'indigo': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'red': 'bg-red-100 text-red-800 border-red-300',
      'gray': 'bg-gray-100 text-gray-600 border-gray-300'
    };
    
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${colorMap[opColor] || 'bg-gray-100'}`}>
        {opLabel}
      </span>
    );
  }
  
  // Fallback status badges
  const styles = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    confirmed: 'bg-green-100 text-green-800 border-green-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300',
    completed: 'bg-blue-100 text-blue-800 border-blue-300'
  };
  const labels = {
    pending: 'In Attesa',
    confirmed: 'Confermata',
    cancelled: 'Cancellata',
    completed: 'Completata'
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${styles[booking.status] || 'bg-gray-100'}`}>
      {labels[booking.status] || booking.status}
    </span>
  );
};

// Payment status badge
const PaymentBadge = ({ booking }) => {
  const total = booking.prezzo_totale || 0;
  const acconto = booking.acconto_importo || 0;
  const saldo = booking.saldo_dovuto ?? (total - acconto);
  
  if (saldo <= 0 && total > 0) {
    return (
      <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium">
        <Check className="w-3 h-3" />
        Saldato
      </span>
    );
  }
  
  if (acconto > 0) {
    return (
      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">
        Parziale
      </span>
    );
  }
  
  if (total > 0) {
    return (
      <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">
        Da pagare
      </span>
    );
  }
  
  return null;
};

// ============================================================================
// FILTER BAR COMPONENT - Styled like reference image
// ============================================================================

const FilterBar = ({ 
  filters, 
  setFilters, 
  units, 
  onApply, 
  onReset,
  bookingCount,
  totalCount,
  showAdvancedFilters,
  setShowAdvancedFilters
}) => {
  return (
    <div className="bg-white rounded-lg border p-4 mb-4">
      {/* Primary filters row */}
      <div className="flex flex-wrap items-end gap-4">
        {/* Date criteria */}
        <div className="min-w-[140px]">
          <Label className="text-sm text-gray-600 mb-1.5 block">Data di</Label>
          <select
            className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={filters.dateCriteria}
            onChange={(e) => setFilters(prev => ({ ...prev, dateCriteria: e.target.value }))}
            data-testid="filter-date-criteria"
          >
            <option value="arrivo">Arrivo</option>
            <option value="partenza">Partenza</option>
            <option value="creazione">Prenotazione</option>
          </select>
        </div>
        
        {/* Date From */}
        <div className="min-w-[160px]">
          <Label className="text-sm text-gray-600 mb-1.5 block">Da</Label>
          <Input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            className="h-10 rounded-lg"
            data-testid="filter-date-from"
          />
        </div>
        
        {/* Date To */}
        <div className="min-w-[160px]">
          <Label className="text-sm text-gray-600 mb-1.5 block">Fino a</Label>
          <Input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
            className="h-10 rounded-lg"
            data-testid="filter-date-to"
          />
        </div>
        
        {/* Altri filtri button */}
        <Button
          variant="outline"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className={`h-10 rounded-lg border-blue-500 text-blue-600 hover:bg-blue-50 ${showAdvancedFilters ? 'bg-blue-50' : ''}`}
          data-testid="filter-advanced-toggle"
        >
          <Filter className="w-4 h-4 mr-2" />
          Altri filtri
          <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showAdvancedFilters ? 'rotate-90' : ''}`} />
        </Button>
        
        {/* Apply button */}
        <Button 
          onClick={onApply}
          className="h-10 rounded-lg bg-blue-600 hover:bg-blue-700 px-6"
          data-testid="filter-apply"
        >
          Mostra
        </Button>
        
        {/* Reset button */}
        <Button 
          variant="ghost" 
          onClick={onReset}
          className="h-10 text-gray-500 hover:text-gray-700"
          data-testid="filter-reset"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset filtri
        </Button>
      </div>
      
      {/* Advanced filters (collapsible) */}
      {showAdvancedFilters && (
        <div className="mt-4 pt-4 border-t flex flex-wrap items-end gap-4">
          {/* Search */}
          <div className="min-w-[250px] flex-1">
            <Label className="text-sm text-gray-600 mb-1.5 block">Ricerca</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Nome, email, telefono, codice OTA..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 h-10 rounded-lg"
                data-testid="filter-search"
              />
            </div>
          </div>
          
          {/* Stato */}
          <div className="min-w-[140px]">
            <Label className="text-sm text-gray-600 mb-1.5 block">Stato</Label>
            <select
              className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              data-testid="filter-status"
            >
              <option value="all">Tutti</option>
              <option value="active">Attive</option>
              <option value="today">Oggi</option>
              <option value="upcoming">Prossime</option>
              <option value="past">Passate</option>
              <option value="cancelled">Cancellate</option>
            </select>
          </div>
          
          {/* Alloggio */}
          <div className="min-w-[160px]">
            <Label className="text-sm text-gray-600 mb-1.5 block">Alloggio</Label>
            <select
              className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm"
              value={filters.unit}
              onChange={(e) => setFilters(prev => ({ ...prev, unit: e.target.value }))}
              data-testid="filter-unit"
            >
              <option value="all">Tutti</option>
              {units.map(u => (
                <option key={u.id} value={u.id}>{u.nome}</option>
              ))}
            </select>
          </div>
          
          {/* Canale */}
          <div className="min-w-[140px]">
            <Label className="text-sm text-gray-600 mb-1.5 block">Canale</Label>
            <select
              className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm"
              value={filters.channel}
              onChange={(e) => setFilters(prev => ({ ...prev, channel: e.target.value }))}
              data-testid="filter-channel"
            >
              <option value="all">Tutti</option>
              <option value="booking">Booking.com</option>
              <option value="airbnb">Airbnb</option>
              <option value="direct">Dirette</option>
            </select>
          </div>
          
          {/* Stato pagamento */}
          <div className="min-w-[140px]">
            <Label className="text-sm text-gray-600 mb-1.5 block">Pagamento</Label>
            <select
              className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm"
              value={filters.paymentStatus}
              onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: e.target.value }))}
              data-testid="filter-payment"
            >
              <option value="all">Tutti</option>
              <option value="paid">Saldati</option>
              <option value="partial">Parziali</option>
              <option value="unpaid">Da pagare</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ECONOMIC SUMMARY COMPONENT - Footer bar style like reference
// ============================================================================

const EconomicSummary = ({ bookings, currentPage, itemsPerPage, totalItems, serverSummary }) => {
  const summary = useMemo(() => {
    // Use server-provided summary if available
    if (serverSummary) {
      return {
        totalePrenotazioni: serverSummary.total_bookings,
        prezzoTotale: serverSummary.gross_total,
        commissioniCosti: serverSummary.commissions_total,
        nettoStimato: serverSummary.estimated_net,
        giaIncassato: serverSummary.collected_total
      };
    }
    
    // Client-side calculation fallback
    let totalePrenotazioni = bookings.length;
    let prezzoTotale = 0;
    let commissioniCosti = 0;
    let giaIncassato = 0;
    
    bookings.forEach(b => {
      prezzoTotale += b.prezzo_totale || 0;
      commissioniCosti += (b.commissioni || 0) + (b.costi_extra || 0);
      giaIncassato += b.acconto_importo || 0;
    });
    
    const nettoStimato = prezzoTotale - commissioniCosti;
    
    return {
      totalePrenotazioni,
      prezzoTotale,
      commissioniCosti,
      nettoStimato,
      giaIncassato
    };
  }, [bookings, serverSummary]);
  
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  
  return (
    <div className="bg-white rounded-lg border mt-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Left side - Count info */}
        <div>
          <p className="font-semibold text-gray-900">Totale prenotazioni: {summary.totalePrenotazioni}</p>
          <p className="text-sm text-gray-500">Risultati {totalItems > 0 ? startItem : 0} - {endItem} di {totalItems}</p>
        </div>
        
        {/* Right side - Economic summary boxes */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Commissione e costi */}
          <div className="bg-gray-100 rounded-lg px-4 py-2 text-center min-w-[140px]">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Commissione e costi</p>
            <p className="text-lg font-bold text-gray-700">{formatCurrency(summary.commissioniCosti)}</p>
          </div>
          
          {/* Prezzo totale */}
          <div className="bg-gray-100 rounded-lg px-4 py-2 text-center min-w-[140px]">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Prezzo totale</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(summary.prezzoTotale)}</p>
          </div>
          
          {/* Incasso netto stimato - highlighted */}
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-center min-w-[160px]">
            <p className="text-xs text-green-700 uppercase tracking-wide">Incasso netto stimato</p>
            <p className="text-lg font-bold text-green-600">{formatCurrency(summary.nettoStimato)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PAGINATION COMPONENT - Style like reference image
// ============================================================================

const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, setItemsPerPage, totalItems }) => {
  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      
      if (currentPage > 3) pages.push('...');
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) pages.push(i);
      
      if (currentPage < totalPages - 2) pages.push('...');
      
      pages.push(totalPages);
    }
    
    return pages;
  };
  
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mt-4 px-1">
      {/* Page numbers */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="text-gray-500 hover:text-gray-700"
          data-testid="pagination-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Indietro
        </Button>
        
        {getPageNumbers().map((page, idx) => (
          page === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
          ) : (
            <Button
              key={page}
              variant={currentPage === page ? "default" : "ghost"}
              size="sm"
              onClick={() => onPageChange(page)}
              className={`min-w-[36px] ${currentPage === page ? 'bg-gray-900 text-white hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid={`pagination-page-${page}`}
            >
              {page}
            </Button>
          )
        ))}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
          data-testid="pagination-next"
        >
          Avanti
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      
      {/* Items per page */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>Mostra</span>
        <select
          className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm"
          value={itemsPerPage}
          onChange={(e) => setItemsPerPage(Number(e.target.value))}
          data-testid="pagination-per-page"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span>per pagina</span>
      </div>
    </div>
  );
};

// ============================================================================
// MOBILE BOOKING CARD COMPONENT
// ============================================================================

const MobileBookingCard = ({ booking, onAction, units }) => {
  const [actionsOpen, setActionsOpen] = useState(false);
  const unit = units.find(u => u.id === booking.unit_id);
  
  return (
    <>
      <Card className="mb-3">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {booking.nationality_flag && (
                <span className="text-lg" title={booking.nationality_name}>
                  {booking.nationality_flag}
                </span>
              )}
              <div>
                <h3 className="font-semibold text-gray-900">{booking.nome_ospite}</h3>
                <ChannelBadge booking={booking} />
              </div>
            </div>
            <StatusBadge booking={booking} />
          </div>
          
          <div className="space-y-2 text-sm">
            {/* Numero ospiti */}
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-4 h-4 text-[#C5A059]" />
              <span>
                {booking.adulti || booking.num_ospiti || 1} {(booking.adulti || booking.num_ospiti || 1) > 1 ? 'adulti' : 'adulto'}
                {booking.bambini > 0 && `, ${booking.bambini} ${booking.bambini > 1 ? 'bambini' : 'bambino'}`}
              </span>
            </div>
            
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-4 h-4 text-[#C5A059]" />
              <span className="font-medium">{formatDateItalian(booking.data_arrivo)}</span>
              <span>→</span>
              <span className="font-medium">{formatDateItalian(booking.data_partenza)}</span>
            </div>
            
            <div className="flex items-center gap-2 text-gray-600">
              <Home className="w-4 h-4" />
              <span>{unit?.nome || booking.unit_nome}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Euro className="w-4 h-4 text-[#C5A059]" />
                <span className="font-bold text-lg">{formatCurrency(booking.prezzo_totale)}</span>
              </div>
              <PaymentBadge booking={booking} />
            </div>
            
            {/* Codice prenotazione - cliccabile per copiare */}
            {booking.codice_prenotazione && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(booking.codice_prenotazione);
                  toast.success('Codice copiato!');
                }}
                className="flex items-center gap-2 text-gray-500 text-xs hover:text-[#C5A059] transition-colors"
              >
                <Key className="w-3 h-3" />
                <span className="font-mono">{booking.codice_prenotazione}</span>
                <Copy className="w-3 h-3 opacity-50" />
              </button>
            )}
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button
              size="sm"
              className="flex-1 bg-[#1B4332] hover:bg-[#1B4332]/90"
              onClick={() => onAction('view', booking)}
              data-testid={`mobile-view-${booking.id}`}
            >
              <Eye className="w-4 h-4 mr-1" />
              Apri
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => setActionsOpen(true)}
              data-testid={`mobile-actions-${booking.id}`}
            >
              <MoreVertical className="w-4 h-4 mr-1" />
              Azioni
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Mobile Actions Bottom Sheet */}
      <Sheet open={actionsOpen} onOpenChange={setActionsOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[80vh] rounded-t-xl">
          <SheetHeader className="text-left pb-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              {booking.nationality_flag && <span>{booking.nationality_flag}</span>}
              {booking.nome_ospite}
            </SheetTitle>
            <SheetDescription>
              {formatDateItalian(booking.data_arrivo)} → {formatDateItalian(booking.data_partenza)}
            </SheetDescription>
          </SheetHeader>
          
          <div className="py-4 space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base"
              onClick={() => { onAction('view', booking); setActionsOpen(false); }}
            >
              <Eye className="w-5 h-5 mr-3 text-gray-500" />
              Visualizza dettagli
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base"
              onClick={() => { onAction('edit', booking); setActionsOpen(false); }}
            >
              <Edit className="w-5 h-5 mr-3 text-gray-500" />
              Modifica prenotazione
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base"
              onClick={() => { onAction('payment', booking); setActionsOpen(false); }}
            >
              <Wallet className="w-5 h-5 mr-3 text-emerald-600" />
              Gestione pagamenti
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base"
              onClick={() => { onAction('messages', booking); setActionsOpen(false); }}
            >
              <MessageSquare className="w-5 h-5 mr-3 text-green-600" />
              Messaggi / WhatsApp
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base"
              onClick={() => { onAction('timeline', booking); setActionsOpen(false); }}
            >
              <Activity className="w-5 h-5 mr-3 text-purple-600" />
              Cronologia comunicazioni
            </Button>
            {booking.status === 'confirmed' && !booking.checkin_online_completato && (
              <Button
                variant="ghost"
                className="w-full justify-start h-12 text-base"
                onClick={() => { onAction('checkin', booking); setActionsOpen(false); }}
              >
                <DoorOpen className="w-5 h-5 mr-3 text-indigo-600" />
                Valida Check-in
              </Button>
            )}
            {booking.is_active && (
              <Button
                variant="ghost"
                className="w-full justify-start h-12 text-base"
                onClick={() => { onAction('changeUnit', booking); setActionsOpen(false); }}
              >
                <Home className="w-5 h-5 mr-3 text-blue-600" />
                Cambia alloggio
              </Button>
            )}
            
            <div className="border-t pt-2 mt-2">
              <Button
                variant="ghost"
                className="w-full justify-start h-12 text-base text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => { onAction('delete', booking); setActionsOpen(false); }}
              >
                <Trash2 className="w-5 h-5 mr-3" />
                Elimina prenotazione
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

// ============================================================================
// DESKTOP TABLE ROW ACTIONS - 4 icon buttons like reference
// ============================================================================

const RowActions = ({ booking, onAction }) => {
  return (
    <div className="flex items-center justify-center gap-1">
      {/* View - Eye icon */}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full"
        onClick={() => onAction('view', booking)}
        title="Visualizza"
        data-testid={`action-view-${booking.id}`}
      >
        <Eye className="w-4 h-4 text-gray-500" />
      </Button>
      
      {/* Edit - Pencil icon */}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full"
        onClick={() => onAction('edit', booking)}
        title="Modifica"
        data-testid={`action-edit-${booking.id}`}
      >
        <Edit className="w-4 h-4 text-gray-500" />
      </Button>
      
      {/* Copy/Document - Receipt icon */}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full"
        onClick={() => {
          if (booking.codice_prenotazione) {
            navigator.clipboard.writeText(booking.codice_prenotazione);
            toast.success('Codice copiato!');
          }
        }}
        title="Copia codice"
        data-testid={`action-copy-${booking.id}`}
      >
        <Copy className="w-4 h-4 text-gray-500" />
      </Button>
      
      {/* More actions dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full"
            data-testid={`action-more-${booking.id}`}
          >
            <MoreVertical className="w-4 h-4 text-gray-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-xs text-gray-500 uppercase">Azioni rapide</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={() => onAction('messages', booking)} className="cursor-pointer">
            <MessageSquare className="w-4 h-4 mr-2 text-green-600" />
            Messaggi / WhatsApp
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => onAction('payment', booking)} className="cursor-pointer">
            <Wallet className="w-4 h-4 mr-2 text-emerald-600" />
            Pagamenti / Acconto
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => onAction('timeline', booking)} className="cursor-pointer">
            <Activity className="w-4 h-4 mr-2 text-purple-600" />
            Cronologia comunicazioni
          </DropdownMenuItem>
          
          {booking.status === 'confirmed' && !booking.checkin_online_completato && (
            <DropdownMenuItem onClick={() => onAction('checkin', booking)} className="cursor-pointer">
              <DoorOpen className="w-4 h-4 mr-2 text-indigo-600" />
              Valida Check-in
            </DropdownMenuItem>
          )}
          
          {booking.is_active && (
            <DropdownMenuItem onClick={() => onAction('changeUnit', booking)} className="cursor-pointer">
              <Home className="w-4 h-4 mr-2 text-blue-600" />
              Cambia alloggio
            </DropdownMenuItem>
          )}
          
          <DropdownMenuItem onClick={() => onAction('expenses', booking)} className="cursor-pointer">
            <Receipt className="w-4 h-4 mr-2 text-gray-600" />
            Spese dettagliate
          </DropdownMenuItem>
          
          {booking.guest_id && (
            <DropdownMenuItem onClick={() => onAction('crm', booking)} className="cursor-pointer">
              <ExternalLink className="w-4 h-4 mr-2 text-gray-600" />
              Profilo CRM ospite
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={() => onAction('delete', booking)}
            className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Elimina prenotazione
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// ============================================================================
// SORTABLE TABLE HEADER COMPONENT
// ============================================================================

const SortableTableHeader = ({ column, children, className = '', sortConfig, onSort }) => (
  <TableHead 
    className={`cursor-pointer hover:bg-gray-50 select-none whitespace-nowrap ${className}`}
    onClick={() => onSort(column)}
  >
    <div className="flex items-center gap-1">
      {children}
      {sortConfig.column === column && (
        <span className="text-blue-600">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
      )}
    </div>
  </TableHead>
);

// ============================================================================
// DESKTOP TABLE COMPONENT - Styled like reference image
// ============================================================================

const BookingsTable = ({ bookings, units, onAction, sortConfig, onSort }) => {
  // Format date with time for booking date column
  const formatDateWithTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      const day = date.getDate();
      const months = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const time = date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
      return (
        <div>
          <div>{day} {month} {year}</div>
          <div className="text-xs text-gray-400">{time}</div>
        </div>
      );
    } catch {
      return dateStr;
    }
  };
  
  // Format arrival/departure date
  const formatBookingDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      const day = date.getDate();
      const months = ['lug', 'ago', 'set', 'ott', 'nov', 'dic', 'gen', 'feb', 'mar', 'apr', 'mag', 'giu'];
      const monthNames = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
      return `${day} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    } catch {
      return dateStr;
    }
  };
  
  // Get payment method text
  const getPaymentMethodText = (booking) => {
    const source = getBookingSource(booking);
    if (source === 'booking') return 'Pagamento tramite Booking.com';
    if (source === 'airbnb') return 'Pagamento tramite Airbnb';
    if (booking.acconto_metodo) {
      const methods = {
        'bonifico': 'Bonifico',
        'carta': 'Carta di credito',
        'contanti': 'Contanti',
        'paypal': 'PayPal'
      };
      return methods[booking.acconto_metodo] || booking.acconto_metodo;
    }
    return 'Pagamento alla struttura';
  };
  
  return (
    <div className="rounded-lg border bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/80">
              <SortableTableHeader column="nome_ospite" className="min-w-[200px]" sortConfig={sortConfig} onSort={onSort}>Nome dell&apos;ospite</SortableTableHeader>
              <SortableTableHeader column="data_arrivo" className="min-w-[100px]" sortConfig={sortConfig} onSort={onSort}>Arrivo</SortableTableHeader>
              <TableHead className="min-w-[100px]">Partenza</TableHead>
              <TableHead className="min-w-[140px]">Alloggi</TableHead>
              <SortableTableHeader column="created_at" className="min-w-[120px]" sortConfig={sortConfig} onSort={onSort}>Data di prenotazione</SortableTableHeader>
              <TableHead className="min-w-[140px]">Stato</TableHead>
              <SortableTableHeader column="prezzo_totale" className="min-w-[90px]" sortConfig={sortConfig} onSort={onSort}>Prezzo</SortableTableHeader>
              <TableHead className="min-w-[100px]">Commissione e costi</TableHead>
              <TableHead className="min-w-[130px]">Numero prenotazione</TableHead>
              <TableHead className="sticky right-0 bg-gray-50/80 min-w-[140px] text-center">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-12 text-gray-500">
                  Nessuna prenotazione trovata
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => {
                const unit = units.find(u => u.id === booking.unit_id);
                const source = getBookingSource(booking);
                const adulti = booking.adulti || booking.num_ospiti || 1;
                const bambini = booking.bambini || 0;
                
                return (
                  <TableRow 
                    key={booking.id}
                    className="hover:bg-blue-50/30 border-b"
                    data-testid={`booking-row-${booking.id}`}
                  >
                    {/* Nome dell'ospite */}
                    <TableCell className="py-3">
                      <div className="space-y-1">
                        <a 
                          href="#" 
                          onClick={(e) => { e.preventDefault(); onAction('view', booking); }}
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {booking.nome_ospite}
                        </a>
                        {/* Guest count */}
                        <div className="text-xs text-gray-500">
                          {adulti} {adulti > 1 ? 'adulti' : 'adulto'}
                          {bambini > 0 && `, ${bambini} ${bambini > 1 ? 'bambini' : 'bambino'}`}
                        </div>
                        {/* Channel badge */}
                        <div className="pt-1">
                          <ChannelBadge booking={booking} />
                        </div>
                      </div>
                    </TableCell>
                    
                    {/* Arrivo */}
                    <TableCell className="font-medium text-gray-900">
                      {formatBookingDate(booking.data_arrivo)}
                    </TableCell>
                    
                    {/* Partenza */}
                    <TableCell className="text-gray-700">
                      {formatBookingDate(booking.data_partenza)}
                    </TableCell>
                    
                    {/* Alloggi */}
                    <TableCell>
                      <div>
                        <div className="font-medium text-gray-900">{unit?.nome || booking.unit_nome || '-'}</div>
                        <div className="text-xs text-gray-500">Appartamento</div>
                      </div>
                    </TableCell>
                    
                    {/* Data di prenotazione */}
                    <TableCell className="text-gray-600 text-sm">
                      {formatDateWithTime(booking.created_at)}
                    </TableCell>
                    
                    {/* Stato */}
                    <TableCell>
                      <div className="space-y-1">
                        <StatusBadge booking={booking} />
                        <div className="text-xs text-gray-500">
                          {getPaymentMethodText(booking)}
                        </div>
                      </div>
                    </TableCell>
                    
                    {/* Prezzo */}
                    <TableCell>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(booking.prezzo_totale)}
                        </div>
                        {booking.acconto_metodo && (
                          <div className="text-xs text-gray-500 capitalize">
                            {booking.acconto_metodo}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    {/* Commissione e costi */}
                    <TableCell className="text-gray-600">
                      {(booking.commissioni || 0) + (booking.costi_extra || 0) > 0 
                        ? formatCurrency((booking.commissioni || 0) + (booking.costi_extra || 0))
                        : '—'
                      }
                    </TableCell>
                    
                    {/* Numero prenotazione */}
                    <TableCell>
                      {booking.codice_prenotazione ? (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(booking.codice_prenotazione);
                            toast.success('Codice copiato!');
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm"
                          title="Clicca per copiare"
                        >
                          {booking.codice_prenotazione}
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    
                    {/* Azioni - Sticky */}
                    <TableCell className="sticky right-0 bg-white border-l">
                      <RowActions booking={booking} onAction={onAction} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AdminBookingsRedesign() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // View state
  const [activeTab, setActiveTab] = useState('bookings');
  const [viewMode, setViewMode] = useState('calendar');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [units, setUnits] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [pricePeriods, setPricePeriods] = useState([]);
  const [dateBlocks, setDateBlocks] = useState([]);
  
  // Financial settings for tax calculation
  const [financialSettings, setFinancialSettings] = useState({
    commissione_booking: 15.0,
    commissione_airbnb: 3.0,
    costo_transazione: 1.5,
    cedolare_secca: 21.0
  });
  const [touristTaxSettings, setTouristTaxSettings] = useState({
    enabled: true,
    tariffs: [],
    default_rate: 3.0 // €3 per adulto/notte default
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    dateCriteria: 'arrivo',
    dateFrom: '',
    dateTo: '',
    status: 'active',
    unit: 'all',
    channel: 'all',
    paymentStatus: 'all'
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ column: 'data_arrivo', direction: 'asc' });
  
  // Advanced filters visibility
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Dialogs (preserved from original)
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [selectedBookingTimeline, setSelectedBookingTimeline] = useState(null);
  const [accontoDialogOpen, setAccontoDialogOpen] = useState(false);
  const [selectedBookingAcconto, setSelectedBookingAcconto] = useState(null);
  const [expensesDialogOpen, setExpensesDialogOpen] = useState(false);
  const [selectedBookingExpenses, setSelectedBookingExpenses] = useState(null);
  const [changeUnitDialogOpen, setChangeUnitDialogOpen] = useState(false);
  const [selectedBookingForUnitChange, setSelectedBookingForUnitChange] = useState(null);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  
  // Forms (preserved from original)
  const [bookingForm, setBookingForm] = useState({
    unit_id: '', data_arrivo: '', data_partenza: '', num_ospiti: 2,
    guest_id: '', nome_ospite: '', email_ospite: '', telefono_ospite: '', note: '', 
    prezzo_totale: '', status: 'confirmed',
    preferred_language: null, language_detection_mode: 'automatic'
  });
  const [editForm, setEditForm] = useState({
    nome_ospite: '', email_ospite: '', telefono_ospite: '',
    data_arrivo: '', data_partenza: '', 
    adulti: 2, bambini: 0,  // Separati per calcolo tassa soggiorno
    note: '', prezzo_totale: '', status: 'confirmed',
    applica_cedolare: true, preferred_language: null, language_detection_mode: 'automatic'
  });
  const [accontoForm, setAccontoForm] = useState({ importo: '', metodo: 'bonifico', note: '' });
  const [priceForm, setPriceForm] = useState({ unit_id: '', nome_periodo: '', data_inizio: '', data_fine: '', prezzo: 0 });
  const [blockForm, setBlockForm] = useState({ unit_id: '', data_inizio: '', data_fine: '', motivo: '' });
  
  // Client search
  const [clienteMode, setClienteMode] = useState('nuovo');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Unit change
  const [availableUnits, setAvailableUnits] = useState([]);
  const [selectedNewUnit, setSelectedNewUnit] = useState('');
  const [unitChangeReason, setUnitChangeReason] = useState('');
  const [unitChangeMode, setUnitChangeMode] = useState('internal_only');
  const [overrideConflict, setOverrideConflict] = useState(false);
  const [changingUnit, setChangingUnit] = useState(false);
  
  // Loading states
  const [savingAcconto, setSavingAcconto] = useState(false);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  
  // Server-side pagination state
  const [serverPagination, setServerPagination] = useState({
    totalItems: 0,
    totalPages: 1,
    filteredSummary: {
      total_bookings: 0,
      gross_total: 0,
      commissions_total: 0,
      estimated_net: 0,
      collected_total: 0,
      outstanding_total: 0
    }
  });

  // ============================================================================
  // DATA FETCHING - V2 with server-side pagination
  // ============================================================================
  
  const fetchBookingsV2 = useCallback(async () => {
    setLoading(true);
    try {
      // Build V2 query params
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        status: filters.status,
        date_type: filters.dateCriteria,
        sort_by: sortConfig.column,
        sort_order: sortConfig.direction
      });
      
      if (filters.search?.trim()) params.append('search', filters.search.trim());
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.unit && filters.unit !== 'all') params.append('unit_id', filters.unit);
      if (filters.channel && filters.channel !== 'all') params.append('channel', filters.channel);
      if (filters.paymentStatus && filters.paymentStatus !== 'all') params.append('payment_status', filters.paymentStatus);
      
      const [unitsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/units`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bookings/v2?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setUnits(unitsRes.data);
      setBookings(bookingsRes.data.items);
      setServerPagination({
        totalItems: bookingsRes.data.total_items,
        totalPages: bookingsRes.data.total_pages,
        filteredSummary: bookingsRes.data.filtered_summary
      });
      
      // Load optional data in background
      Promise.all([
        axios.get(`${API}/admin/price-periods`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/date-blocks`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]).then(([pricesRes, blocksRes]) => {
        setPricePeriods(pricesRes.data);
        setDateBlocks(blocksRes.data);
      });
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, itemsPerPage, filters, sortConfig]);
  
  // Legacy fetchAll for calendar view (uses old endpoint)
  const fetchAll = useCallback(async () => {
    try {
      let bookingsUrl = `${API}/admin/bookings?filter=${filters.status}`;
      if (filters.dateFrom) bookingsUrl += `&data_arrivo_from=${filters.dateFrom}`;
      if (filters.dateTo) bookingsUrl += `&data_arrivo_to=${filters.dateTo}`;
      
      const [unitsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/units`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(bookingsUrl, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setUnits(unitsRes.data);
      setBookings(bookingsRes.data);
      
      // Load optional data
      try {
        const pricesRes = await axios.get(`${API}/admin/price-periods`, { headers: { Authorization: `Bearer ${token}` } });
        setPricePeriods(pricesRes.data);
      } catch (e) { setPricePeriods([]); }
      
      try {
        const blocksRes = await axios.get(`${API}/admin/date-blocks`, { headers: { Authorization: `Bearer ${token}` } });
        setDateBlocks(blocksRes.data);
      } catch (e) { setDateBlocks([]); }
      
      // Load financial settings for tax calculations
      try {
        const finRes = await axios.get(`${API}/admin/financial/settings`, { headers: { Authorization: `Bearer ${token}` } });
        if (finRes.data) setFinancialSettings(finRes.data);
      } catch (e) { console.log('Financial settings not loaded'); }
      
      try {
        const taxRes = await axios.get(`${API}/admin/economic/tourist-tax/settings`, { headers: { Authorization: `Bearer ${token}` } });
        if (taxRes.data) setTouristTaxSettings(taxRes.data);
      } catch (e) { console.log('Tourist tax settings not loaded'); }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [token, filters.status, filters.dateFrom, filters.dateTo]);
  
  // Fetch based on view mode
  useEffect(() => {
    if (viewMode === 'list') {
      fetchBookingsV2();
    } else {
      fetchAll();
    }
  }, [viewMode, fetchBookingsV2, fetchAll]);

  // Handle URL parameter to open a specific booking
  useEffect(() => {
    const openBookingId = searchParams.get('open');
    if (openBookingId && bookings.length > 0 && !editDialogOpen) {
      const bookingToOpen = bookings.find(b => b.id === openBookingId);
      if (bookingToOpen) {
        setEditingBooking(bookingToOpen);
        setEditDialogOpen(true);
        // Clear the URL parameter
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('open');
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [searchParams, bookings, editDialogOpen, setSearchParams]);

  // ============================================================================
  // FILTERING & SORTING
  // ============================================================================
  
  const filteredBookings = useMemo(() => {
    let result = [...bookings];
    
    // Search filter
    if (filters.search.trim()) {
      const query = filters.search.toLowerCase().trim();
      result = result.filter(b => {
        const nome = (b.nome_ospite || '').toLowerCase();
        const email = (b.email_ospite || '').toLowerCase();
        const telefono = (b.telefono_ospite || '').toLowerCase();
        const codice = (b.codice_prenotazione || '').toLowerCase();
        const beds24Ref = (b.beds24_reference || '').toLowerCase();
        return nome.includes(query) || email.includes(query) || 
               telefono.includes(query) || codice.includes(query) ||
               beds24Ref.includes(query);
      });
    }
    
    // Unit filter
    if (filters.unit !== 'all') {
      result = result.filter(b => b.unit_id === filters.unit);
    }
    
    // Channel filter
    if (filters.channel !== 'all') {
      result = result.filter(b => getBookingSource(b) === filters.channel);
    }
    
    // Payment status filter
    if (filters.paymentStatus !== 'all') {
      result = result.filter(b => {
        const total = b.prezzo_totale || 0;
        const acconto = b.acconto_importo || 0;
        const saldo = b.saldo_dovuto ?? (total - acconto);
        
        if (filters.paymentStatus === 'paid') return saldo <= 0 && total > 0;
        if (filters.paymentStatus === 'partial') return acconto > 0 && saldo > 0;
        if (filters.paymentStatus === 'unpaid') return acconto === 0 && total > 0;
        return true;
      });
    }
    
    // Sorting
    result.sort((a, b) => {
      let aVal = a[sortConfig.column];
      let bVal = b[sortConfig.column];
      
      if (sortConfig.column.includes('data') || sortConfig.column === 'created_at') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else if (sortConfig.column === 'prezzo_totale') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [bookings, filters, sortConfig]);
  
  // Pagination - Use server-side in list view, client-side for calendar
  const totalPages = viewMode === 'list' 
    ? serverPagination.totalPages 
    : Math.ceil(filteredBookings.length / itemsPerPage);
  
  const paginatedBookings = viewMode === 'list'
    ? bookings  // Already paginated from server
    : filteredBookings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  
  const totalFilteredItems = viewMode === 'list'
    ? serverPagination.totalItems
    : filteredBookings.length;
  
  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);  // Reset to page 1 when sorting changes
  };
  
  const handleApplyFilters = () => {
    setCurrentPage(1);
    if (viewMode === 'list') {
      fetchBookingsV2();
    } else {
      fetchAll();
    }
  };
  
  const handleResetFilters = () => {
    setFilters({
      search: '',
      dateCriteria: 'arrivo',
      dateFrom: '',
      dateTo: '',
      status: 'active',
      unit: 'all',
      channel: 'all',
      paymentStatus: 'all'
    });
    setCurrentPage(1);
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  
  // Handle items per page change
  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================
  
  const handleAction = async (action, booking) => {
    switch (action) {
      case 'view':
      case 'edit':
        openEditDialog(booking);
        break;
      case 'payment':
        openAccontoDialog(booking);
        break;
      case 'messages':
        window.location.href = `/admin/whatsapp?booking=${booking.id}`;
        break;
      case 'timeline':
        openTimelineDialog(booking);
        break;
      case 'checkin':
        await validateCheckin(booking.id);
        break;
      case 'changeUnit':
        await openChangeUnitDialog(booking);
        break;
      case 'expenses':
        await loadBookingExpenses(booking.id);
        break;
      case 'crm':
        if (booking.guest_id) {
          window.location.href = `/admin/guests/${booking.guest_id}`;
        }
        break;
      case 'delete':
        if (window.confirm('Eliminare questa prenotazione?')) {
          await deleteBooking(booking.id);
        }
        break;
      default:
        console.log('Unknown action:', action);
    }
  };
  
  // Open edit dialog
  const openEditDialog = (booking) => {
    setEditingBooking(booking);
    const source = getBookingSource(booking);
    const isDirectBooking = ['direct', 'whatsapp', 'telefono'].includes(source);
    const applicaCedolare = booking.applica_cedolare !== undefined 
      ? booking.applica_cedolare 
      : !isDirectBooking;
    
    setEditForm({
      nome_ospite: booking.nome_ospite || '',
      email_ospite: booking.email_ospite || '',
      telefono_ospite: booking.telefono_ospite || '',
      data_arrivo: booking.data_arrivo || '',
      data_partenza: booking.data_partenza || '',
      adulti: booking.adulti || booking.num_adulti || booking.adults || 2,
      bambini: booking.bambini || booking.num_bambini || booking.children || 0,
      note: booking.note || '',
      prezzo_totale: booking.prezzo_totale || '',
      status: booking.status || 'confirmed',
      applica_cedolare: applicaCedolare,
      acconto_importo: booking.acconto_importo || 0,
      acconto_metodo: booking.acconto_metodo || '',
      saldo_dovuto: booking.saldo_dovuto || booking.prezzo_totale || 0,
      source: source,
      codice_prenotazione: booking.codice_prenotazione || '',
      beds24_id: booking.beds24_id || null,
      beds24_reference: booking.beds24_reference || '',
      preferred_language: booking.preferred_language || null,
      language_detection_mode: booking.language_detection_mode || 'automatic'
    });
    setEditDialogOpen(true);
  };
  
  // Save booking edit
  const saveBookingEdit = async () => {
    if (!editingBooking) return;
    try {
      await axios.put(`${API}/admin/bookings/${editingBooking.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prenotazione aggiornata!');
      setEditDialogOpen(false);
      setEditingBooking(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };
  
  // Delete booking
  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prenotazione eliminata!');
      fetchAll();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };
  
  // Validate check-in
  const validateCheckin = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/checkins/validate/${bookingId}`, {
        note: 'Documenti ricevuti via canale esterno'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Check-in validato!');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella validazione');
    }
  };
  
  // Open timeline dialog
  const openTimelineDialog = (booking) => {
    setSelectedBookingTimeline(booking);
    setTimelineDialogOpen(true);
  };
  
  // Open acconto dialog
  const openAccontoDialog = (booking) => {
    setSelectedBookingAcconto(booking);
    setAccontoForm({ importo: '', metodo: 'bonifico', note: '' });
    setAccontoDialogOpen(true);
  };
  
  // Handle acconto submit
  const handleAccontoSubmit = async (e) => {
    e.preventDefault();
    if (!accontoForm.importo || parseFloat(accontoForm.importo) <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    setSavingAcconto(true);
    try {
      await axios.post(
        `${API}/admin/bookings/${selectedBookingAcconto.id}/acconto`,
        {
          importo: parseFloat(accontoForm.importo),
          metodo: accontoForm.metodo,
          note: accontoForm.note || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Acconto di €${accontoForm.importo} registrato!`);
      setAccontoDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore registrazione acconto');
    } finally {
      setSavingAcconto(false);
    }
  };
  
  // Load expenses
  const loadBookingExpenses = async (bookingId) => {
    try {
      setLoadingExpenses(true);
      const response = await axios.get(`${API}/admin/bookings/${bookingId}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBookingExpenses(response.data);
      setExpensesDialogOpen(true);
    } catch (error) {
      toast.error('Errore caricamento spese');
    } finally {
      setLoadingExpenses(false);
    }
  };
  
  // Open change unit dialog
  const openChangeUnitDialog = async (booking) => {
    setSelectedBookingForUnitChange(booking);
    setSelectedNewUnit('');
    setUnitChangeReason('');
    setUnitChangeMode('internal_only');
    setOverrideConflict(false);
    setChangeUnitDialogOpen(true);
    
    try {
      const response = await axios.get(
        `${API}/admin/bookings/${booking.id}/available-units`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableUnits(response.data.units || []);
    } catch (error) {
      toast.error('Errore caricamento unità disponibili');
      setAvailableUnits([]);
    }
  };
  
  // Handle unit change
  const handleUnitChange = async () => {
    if (!selectedNewUnit) {
      toast.error('Seleziona una nuova unità');
      return;
    }
    
    const targetUnit = availableUnits.find(u => u.id === selectedNewUnit);
    if (targetUnit && !targetUnit.is_available && !overrideConflict) {
      toast.error('Unità non disponibile. Usa override per procedere.');
      return;
    }
    
    if (overrideConflict && !unitChangeReason.trim()) {
      toast.error('Il motivo è obbligatorio quando si usa override');
      return;
    }
    
    setChangingUnit(true);
    try {
      const response = await axios.patch(
        `${API}/admin/bookings/${selectedBookingForUnitChange.id}/unit`,
        {
          new_unit_id: selectedNewUnit,
          mode: unitChangeMode,
          reason: unitChangeReason,
          override_conflict: overrideConflict
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setChangeUnitDialogOpen(false);
        fetchAll();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore cambio alloggio');
    } finally {
      setChangingUnit(false);
    }
  };
  
  // Client search
  const searchGuests = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/admin/guests/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Errore ricerca:', error);
    } finally {
      setSearchLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) searchGuests(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchGuests]);
  
  const selectGuest = (guest) => {
    setSelectedGuest(guest);
    setBookingForm(prev => ({
      ...prev,
      guest_id: guest.id,
      nome_ospite: guest.nome_completo,
      email_ospite: guest.email,
      telefono_ospite: guest.telefono || ''
    }));
    setSearchQuery('');
    setSearchResults([]);
  };
  
  const clearSelectedGuest = () => {
    setSelectedGuest(null);
    setBookingForm(prev => ({
      ...prev,
      guest_id: '',
      nome_ospite: '',
      email_ospite: '',
      telefono_ospite: ''
    }));
  };
  
  const resetBookingForm = () => {
    setBookingForm({
      unit_id: '', data_arrivo: '', data_partenza: '', num_ospiti: 2,
      guest_id: '', nome_ospite: '', email_ospite: '', telefono_ospite: '', note: '',
      prezzo_totale: '', status: 'confirmed', preferred_language: null, language_detection_mode: 'automatic'
    });
    setClienteMode('nuovo');
    setSelectedGuest(null);
    setSearchQuery('');
    setSearchResults([]);
  };
  
  // Handle new booking submit
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        unit_id: bookingForm.unit_id,
        data_arrivo: bookingForm.data_arrivo,
        data_partenza: bookingForm.data_partenza,
        num_ospiti: parseInt(bookingForm.num_ospiti),
        note: bookingForm.note || null,
        prezzo_totale: bookingForm.prezzo_totale ? parseFloat(bookingForm.prezzo_totale) : null,
        status: bookingForm.status
      };
      
      if (clienteMode === 'esistente' && selectedGuest) {
        payload.guest_id = selectedGuest.id;
      } else {
        payload.nome_ospite = bookingForm.nome_ospite;
        payload.email_ospite = bookingForm.email_ospite;
        payload.telefono_ospite = bookingForm.telefono_ospite;
      }
      
      const response = await axios.post(`${API}/admin/bookings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Prenotazione creata! Codice: ${response.data.codice_prenotazione}`);
      setBookingDialogOpen(false);
      resetBookingForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };
  
  // Price period handlers
  const handlePriceSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/price-periods`, priceForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Periodo prezzo creato!');
      setPriceDialogOpen(false);
      setPriceForm({ unit_id: '', nome_periodo: '', data_inizio: '', data_fine: '', prezzo: 0 });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };
  
  // Date block handlers
  const handleBlockSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/date-blocks`, blockForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Date bloccate!');
      setBlockDialogOpen(false);
      setBlockForm({ unit_id: '', data_inizio: '', data_fine: '', motivo: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };
  
  // Export function - uses backend endpoint
  const handleExport = async (format = 'csv') => {
    try {
      // Build query params with current filters
      const params = new URLSearchParams({
        format,
        status: filters.status,
        date_type: filters.dateCriteria,
        sort_by: sortConfig.column,
        sort_order: sortConfig.direction
      });
      
      if (filters.search?.trim()) params.append('search', filters.search.trim());
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.unit && filters.unit !== 'all') params.append('unit_id', filters.unit);
      if (filters.channel && filters.channel !== 'all') params.append('channel', filters.channel);
      if (filters.paymentStatus && filters.paymentStatus !== 'all') params.append('payment_status', filters.paymentStatus);
      
      const response = await axios.get(`${API}/admin/bookings/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'xlsx' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'text/csv;charset=utf-8' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `prenotazioni.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${format.toUpperCase()} completato!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore durante l\'esportazione');
    }
  };
  
  // Print function
  const handlePrint = () => {
    window.print();
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <AdminLayout title="Gestione Prenotazioni">
      <div className="space-y-4">
        {/* Tabs */}
        <div className="flex gap-2 border-b">
          {[
            { key: 'bookings', label: 'Prenotazioni', icon: Calendar },
            { key: 'prices', label: 'Prezzi', icon: Euro },
            { key: 'blocks', label: 'Blocchi Date', icon: X }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.key 
                  ? 'border-[#C5A059] text-[#C5A059]' 
                  : 'border-transparent text-[#4A5568] hover:text-[#C5A059]'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059] mx-auto"></div>
          </div>
        ) : (
          <>
            {/* BOOKINGS TAB */}
            {activeTab === 'bookings' && (
              <div className="space-y-4">
                {/* Header with view toggle and action buttons */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  {/* View Toggle */}
                  <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => setViewMode('calendar')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md transition font-medium ${
                        viewMode === 'calendar'
                          ? 'bg-[#1B4332] text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid="view-calendar-btn"
                    >
                      <LayoutGrid className="w-4 h-4" />
                      Calendario
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md transition font-medium ${
                        viewMode === 'list'
                          ? 'bg-[#1B4332] text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid="view-list-btn"
                    >
                      <List className="w-4 h-4" />
                      Lista
                    </button>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {/* Export dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="hidden sm:flex"
                          data-testid="export-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Esporta
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => handleExport('csv')}>
                          <FileText className="w-4 h-4 mr-2" />
                          Esporta CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                          <FileText className="w-4 h-4 mr-2" />
                          Esporta Excel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrint}
                      className="hidden sm:flex"
                      data-testid="print-btn"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Stampa
                    </Button>
                    <Button 
                      onClick={() => setBookingDialogOpen(true)}
                      className="bg-[#C5A059] hover:bg-[#B08D45]"
                      data-testid="new-booking-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nuova Prenotazione
                    </Button>
                  </div>
                </div>
                
                {/* CALENDAR VIEW */}
                {viewMode === 'calendar' && (
                  <BookingCalendar
                    units={units}
                    bookings={bookings}
                    blocks={dateBlocks}
                    prices={pricePeriods}
                    onOpenBooking={(booking) => openEditDialog(booking)}
                    onCreateBooking={(unit, date) => {
                      setBookingForm(prev => ({
                        ...prev,
                        unit_id: unit?.id || '',
                        data_arrivo: date ? date.toISOString().split('T')[0] : '',
                        data_partenza: date ? new Date(date.getTime() + 86400000 * 2).toISOString().split('T')[0] : ''
                      }));
                      setBookingDialogOpen(true);
                    }}
                    onBlockDates={(unit, date) => {
                      setBlockForm(prev => ({
                        ...prev,
                        unit_id: unit?.id || '',
                        data_inizio: date ? date.toISOString().split('T')[0] : '',
                        data_fine: date ? new Date(date.getTime() + 86400000).toISOString().split('T')[0] : ''
                      }));
                      setBlockDialogOpen(true);
                    }}
                    onChangePrice={(unit, date) => {
                      setPriceForm(prev => ({
                        ...prev,
                        unit_id: unit?.id || '',
                        data_inizio: date ? date.toISOString().split('T')[0] : '',
                        data_fine: date ? new Date(date.getTime() + 86400000 * 7).toISOString().split('T')[0] : ''
                      }));
                      setPriceDialogOpen(true);
                    }}
                    onMaintenance={(unit, date) => {
                      setBlockForm(prev => ({
                        ...prev,
                        unit_id: unit?.id || '',
                        data_inizio: date ? date.toISOString().split('T')[0] : '',
                        data_fine: date ? new Date(date.getTime() + 86400000).toISOString().split('T')[0] : '',
                        motivo: 'Manutenzione'
                      }));
                      setBlockDialogOpen(true);
                    }}
                    onChangeRoom={(booking) => openChangeUnitDialog(booking)}
                    onWhatsApp={(booking) => {
                      if (booking.telefono_ospite) {
                        const phone = booking.telefono_ospite.replace(/\D/g, '');
                        window.open(`https://wa.me/${phone}`, '_blank');
                      } else {
                        toast.error('Numero di telefono non disponibile');
                      }
                    }}
                    onCall={(booking) => {
                      if (booking.telefono_ospite) {
                        window.open(`tel:${booking.telefono_ospite}`, '_blank');
                      } else {
                        toast.error('Numero di telefono non disponibile');
                      }
                    }}
                    onOpenTimeline={(booking) => openTimelineDialog(booking)}
                  />
                )}
                
                {/* LIST VIEW */}
                {viewMode === 'list' && (
                  <>
                    {/* Filters */}
                    <FilterBar
                      filters={filters}
                      setFilters={setFilters}
                      units={units}
                      onApply={handleApplyFilters}
                      onReset={handleResetFilters}
                      bookingCount={filteredBookings.length}
                      totalCount={bookings.length}
                      showAdvancedFilters={showAdvancedFilters}
                      setShowAdvancedFilters={setShowAdvancedFilters}
                    />
                    
                    {/* Desktop Table (hidden on mobile) */}
                    <div className="hidden lg:block">
                      <BookingsTable
                        bookings={paginatedBookings}
                        units={units}
                        onAction={handleAction}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </div>
                    
                    {/* Mobile Cards (hidden on desktop) */}
                    <div className="lg:hidden">
                      {paginatedBookings.length === 0 ? (
                        <Card>
                          <CardContent className="py-12 text-center text-gray-500">
                            Nessuna prenotazione trovata
                          </CardContent>
                        </Card>
                      ) : (
                        paginatedBookings.map((booking) => (
                          <MobileBookingCard
                            key={booking.id}
                            booking={booking}
                            units={units}
                            onAction={handleAction}
                          />
                        ))
                      )}
                    </div>
                    
                    {/* Pagination */}
                    {totalFilteredItems > 0 && (
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={handleItemsPerPageChange}
                        totalItems={totalFilteredItems}
                      />
                    )}
                    
                    {/* Economic Summary - uses server data in list view */}
                    <EconomicSummary 
                      bookings={viewMode === 'list' ? bookings : filteredBookings} 
                      currentPage={currentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={totalFilteredItems}
                      serverSummary={viewMode === 'list' ? serverPagination.filteredSummary : null}
                    />
                  </>
                )}
              </div>
            )}

            {/* PRICES TAB - Preserved from original */}
            {activeTab === 'prices' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="font-cinzel text-xl">Prezzi Speciali per Periodo</h2>
                  <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#C5A059] hover:bg-[#B08D45]">
                        <Plus className="w-4 h-4 mr-2" /> Nuovo Periodo
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Nuovo Periodo Prezzo</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handlePriceSubmit} className="space-y-4">
                        <div>
                          <Label>Casetta *</Label>
                          <select
                            className="w-full border rounded-md px-3 py-2"
                            value={priceForm.unit_id}
                            onChange={(e) => setPriceForm({ ...priceForm, unit_id: e.target.value })}
                            required
                          >
                            <option value="">Seleziona...</option>
                            {units.map(u => (
                              <option key={u.id} value={u.id}>{u.nome}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label>Nome Periodo</Label>
                          <Input
                            value={priceForm.nome_periodo}
                            onChange={(e) => setPriceForm({ ...priceForm, nome_periodo: e.target.value })}
                            placeholder="Es. Alta Stagione"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Data Inizio</Label>
                            <Input
                              type="date"
                              value={priceForm.data_inizio}
                              onChange={(e) => setPriceForm({ ...priceForm, data_inizio: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label>Data Fine</Label>
                            <Input
                              type="date"
                              value={priceForm.data_fine}
                              onChange={(e) => setPriceForm({ ...priceForm, data_fine: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Prezzo (€/notte)</Label>
                          <Input
                            type="number"
                            min={0}
                            value={priceForm.prezzo}
                            onChange={(e) => setPriceForm({ ...priceForm, prezzo: parseFloat(e.target.value) })}
                            required
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => setPriceDialogOpen(false)}>
                            Annulla
                          </Button>
                          <Button type="submit" className="bg-[#C5A059] hover:bg-[#B08D45]">
                            Salva
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                
                {pricePeriods.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-[#4A5568]">
                      Nessun periodo prezzo configurato.
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {pricePeriods.map((period) => {
                      const unit = units.find(u => u.id === period.unit_id);
                      return (
                        <Card key={period.id}>
                          <CardContent className="py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <span className="font-medium">{unit?.nome}</span>
                                <span className="text-sm text-[#4A5568]">
                                  {period.nome_periodo}: {period.data_inizio} → {period.data_fine}
                                </span>
                                <span className="font-bold text-[#C5A059]">€{period.prezzo}/notte</span>
                              </div>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600"
                                onClick={async () => {
                                  if (!window.confirm('Eliminare questo periodo?')) return;
                                  try {
                                    await axios.delete(`${API}/admin/price-periods/${period.id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success('Periodo eliminato!');
                                    fetchAll();
                                  } catch (error) {
                                    toast.error('Errore');
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* BLOCKS TAB - Preserved from original */}
            {activeTab === 'blocks' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="font-cinzel text-xl">Date Bloccate</h2>
                  <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#C5A059] hover:bg-[#B08D45]">
                        <Plus className="w-4 h-4 mr-2" /> Blocca Date
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Blocca Date</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleBlockSubmit} className="space-y-4">
                        <div>
                          <Label>Casetta *</Label>
                          <select
                            className="w-full border rounded-md px-3 py-2"
                            value={blockForm.unit_id}
                            onChange={(e) => setBlockForm({ ...blockForm, unit_id: e.target.value })}
                            required
                          >
                            <option value="">Seleziona...</option>
                            {units.map(u => (
                              <option key={u.id} value={u.id}>{u.nome}</option>
                            ))}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Data Inizio</Label>
                            <Input
                              type="date"
                              value={blockForm.data_inizio}
                              onChange={(e) => setBlockForm({ ...blockForm, data_inizio: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label>Data Fine</Label>
                            <Input
                              type="date"
                              value={blockForm.data_fine}
                              onChange={(e) => setBlockForm({ ...blockForm, data_fine: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Motivo (opzionale)</Label>
                          <Input
                            value={blockForm.motivo}
                            onChange={(e) => setBlockForm({ ...blockForm, motivo: e.target.value })}
                            placeholder="Es. Manutenzione, Uso personale..."
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => setBlockDialogOpen(false)}>
                            Annulla
                          </Button>
                          <Button type="submit" className="bg-[#C5A059] hover:bg-[#B08D45]">
                            Blocca
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                
                {dateBlocks.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-[#4A5568]">
                      Nessuna data bloccata.
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {dateBlocks.map((block) => {
                      const unit = units.find(u => u.id === block.unit_id);
                      return (
                        <Card key={block.id}>
                          <CardContent className="py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <span className="font-medium">{unit?.nome}</span>
                                <span className="text-sm text-[#4A5568]">
                                  {block.data_inizio} → {block.data_fine}
                                </span>
                                {block.motivo && (
                                  <span className="text-sm text-[#4A5568] italic">&quot;{block.motivo}&quot;</span>
                                )}
                              </div>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600"
                                onClick={async () => {
                                  if (!window.confirm('Rimuovere il blocco?')) return;
                                  try {
                                    await axios.delete(`${API}/admin/date-blocks/${block.id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    toast.success('Blocco rimosso!');
                                    fetchAll();
                                  } catch (error) {
                                    toast.error('Errore');
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* ================================================================== */}
      {/* DIALOGS - Preserved from original with minor updates              */}
      {/* ================================================================== */}
      
      {/* New Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Prenotazione</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBookingSubmit} className="space-y-4">
            <div>
              <Label>Casetta *</Label>
              <select
                className="w-full border rounded-md px-3 py-2"
                value={bookingForm.unit_id}
                onChange={(e) => setBookingForm({ ...bookingForm, unit_id: e.target.value })}
                required
              >
                <option value="">Seleziona...</option>
                {units.map(u => (
                  <option key={u.id} value={u.id}>{u.nome}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Arrivo *</Label>
                <Input
                  type="date"
                  value={bookingForm.data_arrivo}
                  onChange={(e) => setBookingForm({ ...bookingForm, data_arrivo: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Data Partenza *</Label>
                <Input
                  type="date"
                  value={bookingForm.data_partenza}
                  onChange={(e) => setBookingForm({ ...bookingForm, data_partenza: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Numero Ospiti *</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={bookingForm.num_ospiti}
                  onChange={(e) => setBookingForm({ ...bookingForm, num_ospiti: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Prezzo Totale (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Auto calcolato"
                  value={bookingForm.prezzo_totale}
                  onChange={(e) => setBookingForm({ ...bookingForm, prezzo_totale: e.target.value })}
                />
              </div>
            </div>
            
            {/* Client Selection */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">Cliente</Label>
              <div className="flex gap-2 mb-4">
                <Button
                  type="button"
                  variant={clienteMode === 'esistente' ? 'default' : 'outline'}
                  className={clienteMode === 'esistente' ? 'bg-[#C5A059] hover:bg-[#B08D45]' : ''}
                  onClick={() => { setClienteMode('esistente'); clearSelectedGuest(); }}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Esistente
                </Button>
                <Button
                  type="button"
                  variant={clienteMode === 'nuovo' ? 'default' : 'outline'}
                  className={clienteMode === 'nuovo' ? 'bg-[#C5A059] hover:bg-[#B08D45]' : ''}
                  onClick={() => { setClienteMode('nuovo'); clearSelectedGuest(); }}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Nuovo
                </Button>
              </div>
              
              {clienteMode === 'esistente' ? (
                <div className="space-y-3">
                  {selectedGuest ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#C5A059] rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium">{selectedGuest.nome_completo}</p>
                            <p className="text-sm text-gray-600">{selectedGuest.email}</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearSelectedGuest}
                          className="text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Cerca per nome o email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                      {searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {searchResults.map((guest) => (
                            <button
                              key={guest.id}
                              type="button"
                              onClick={() => selectGuest(guest)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0"
                            >
                              <p className="font-medium text-sm">{guest.nome_completo}</p>
                              <p className="text-xs text-gray-500">{guest.email}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>Nome Ospite *</Label>
                    <Input
                      value={bookingForm.nome_ospite}
                      onChange={(e) => setBookingForm({ ...bookingForm, nome_ospite: e.target.value })}
                      required={clienteMode === 'nuovo'}
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={bookingForm.email_ospite}
                        onChange={(e) => setBookingForm({ ...bookingForm, email_ospite: e.target.value })}
                        required={clienteMode === 'nuovo'}
                        placeholder="email@esempio.com"
                      />
                    </div>
                    <div>
                      <Label>Telefono</Label>
                      <Input
                        type="tel"
                        value={bookingForm.telefono_ospite}
                        onChange={(e) => setBookingForm({ ...bookingForm, telefono_ospite: e.target.value })}
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <Label>Note</Label>
              <Textarea
                value={bookingForm.note}
                onChange={(e) => setBookingForm({ ...bookingForm, note: e.target.value })}
                rows={2}
                placeholder="Note aggiuntive..."
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setBookingDialogOpen(false); resetBookingForm(); }}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="bg-[#C5A059] hover:bg-[#B08D45]"
                disabled={clienteMode === 'esistente' && !selectedGuest}
              >
                Crea Prenotazione
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit Booking Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 bg-[#C5A059] rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <span>Gestisci Prenotazione</span>
                {editForm.codice_prenotazione && (
                  <span className="ml-2 text-sm font-normal text-gray-500">#{editForm.codice_prenotazione}</span>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-6 py-4">
            {/* Booking Source */}
            <div className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 border">
              <span className="text-gray-600 font-medium">Fonte:</span>
              {editForm.source === 'booking' && (
                <span className="flex items-center gap-2 bg-[#003580] text-white px-4 py-2 rounded-full font-bold">
                  Booking.com
                </span>
              )}
              {editForm.source === 'airbnb' && (
                <span className="flex items-center gap-2 bg-[#FF5A5F] text-white px-4 py-2 rounded-full font-bold">
                  Airbnb
                </span>
              )}
              {['direct', 'whatsapp', 'telefono'].includes(editForm.source) && (
                <span className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-full font-bold">
                  Diretta
                </span>
              )}
            </div>

            {/* Guest Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <User className="w-5 h-5 text-[#C5A059]" />
                Dati Ospite
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Nome Completo *</Label>
                  <Input
                    value={editForm.nome_ospite}
                    onChange={(e) => setEditForm({...editForm, nome_ospite: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.email_ospite}
                    onChange={(e) => setEditForm({...editForm, email_ospite: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input
                    value={editForm.telefono_ospite}
                    onChange={(e) => setEditForm({...editForm, telefono_ospite: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Stay Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C5A059]" />
                Dettagli Soggiorno
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <Label>Check-in</Label>
                  <Input
                    type="date"
                    value={editForm.data_arrivo}
                    onChange={(e) => setEditForm({...editForm, data_arrivo: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Check-out</Label>
                  <Input
                    type="date"
                    value={editForm.data_partenza}
                    onChange={(e) => setEditForm({...editForm, data_partenza: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Adulti</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={editForm.adulti}
                    onChange={(e) => setEditForm({...editForm, adulti: parseInt(e.target.value) || 1})}
                    className="mt-1"
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Tassa soggiorno applicata</p>
                </div>
                <div>
                  <Label>Bambini</Label>
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={editForm.bambini}
                    onChange={(e) => setEditForm({...editForm, bambini: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Esenti da tassa</p>
                </div>
                <div>
                  <Label>Stato</Label>
                  <select
                    className="w-full h-10 mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={editForm.status}
                    onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                  >
                    <option value="pending">In Attesa</option>
                    <option value="confirmed">Confermata</option>
                    <option value="completed">Completata</option>
                    <option value="cancelled">Cancellata</option>
                  </select>
                </div>
              </div>
              {editingBooking?.unit_nome && (
                <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                  <Home className="w-4 h-4" />
                  <span>Alloggio: <strong>{editingBooking.unit_nome}</strong></span>
                </div>
              )}
              
              {/* Calcolo Tassa di Soggiorno in tempo reale */}
              {(() => {
                const numNotti = editForm.data_arrivo && editForm.data_partenza 
                  ? Math.max(1, Math.ceil((new Date(editForm.data_partenza) - new Date(editForm.data_arrivo)) / (1000 * 60 * 60 * 24)))
                  : 1;
                const tassa = calculateTouristTax(
                  editForm.data_arrivo, 
                  editForm.adulti || 1, 
                  numNotti, 
                  touristTaxSettings?.tariffs || [],
                  touristTaxSettings?.default_rate || 3.0
                );
                
                // Check if this is an OTA booking
                const source = editForm.source || getBookingSource(editingBooking || {});
                const isOTA = ['booking', 'airbnb', 'expedia'].includes(source?.toLowerCase() || '');
                
                if (tassa > 0 && isOTA) {
                  return (
                    <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 mt-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-cyan-600" />
                          <span className="text-sm font-medium text-cyan-700">Tassa di Soggiorno</span>
                        </div>
                        <span className="text-lg font-bold text-cyan-700">€{tassa.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-cyan-600 mt-1">
                        {editForm.adulti} adult{editForm.adulti > 1 ? 'i' : 'o'} × {numNotti} nott{numNotti > 1 ? 'i' : 'e'} × €{(touristTaxSettings?.default_rate || 3).toFixed(2)}
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Payment Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Euro className="w-5 h-5 text-[#C5A059]" />
                Pagamento
              </h3>
              
              {/* Calcolo importi con detrazioni */}
              {(() => {
                const lordo = parseFloat(editForm.prezzo_totale) || 0;
                const source = editForm.source || getBookingSource(editingBooking || {});
                const isOTA = ['booking', 'airbnb', 'expedia'].includes(source?.toLowerCase() || '');
                
                // Calcola notti
                const numNotti = editForm.data_arrivo && editForm.data_partenza 
                  ? Math.max(1, Math.ceil((new Date(editForm.data_partenza) - new Date(editForm.data_arrivo)) / (1000 * 60 * 60 * 24)))
                  : 1;
                
                // Tassa soggiorno (solo OTA)
                const tassaSoggiorno = isOTA ? calculateTouristTax(
                  editForm.data_arrivo, 
                  editForm.adulti || 1, 
                  numNotti, 
                  touristTaxSettings?.tariffs || [],
                  touristTaxSettings?.default_rate || 3.0
                ) : 0;
                
                // Calcola netto
                const netto = calculateNetAmount(lordo, source, financialSettings, tassaSoggiorno);
                const acconto = parseFloat(editForm.acconto_importo) || 0;
                const saldoDovuto = Math.max(0, netto - acconto);
                
                return (
                  <>
                    {/* Riepilogo detrazioni per OTA */}
                    {isOTA && lordo > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1 border">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Lordo prenotazione</span>
                          <span className="font-medium">{formatCurrency(lordo)}</span>
                        </div>
                        {tassaSoggiorno > 0 && (
                          <div className="flex justify-between text-cyan-600">
                            <span>- Tassa Soggiorno</span>
                            <span>-{formatCurrency(tassaSoggiorno)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-gray-500">
                          <span>= Imponibile</span>
                          <span>{formatCurrency(lordo - tassaSoggiorno)}</span>
                        </div>
                        <div className="flex justify-between text-yellow-600">
                          <span>- IVA {financialSettings?.iva_percent || 10}%</span>
                          <span>-{formatCurrency((lordo - tassaSoggiorno) * (financialSettings?.iva_percent || 10) / 100)}</span>
                        </div>
                        <div className="flex justify-between text-orange-600">
                          <span>- Comm. OTA ({source?.toLowerCase() === 'airbnb' ? (financialSettings?.commissione_airbnb || 3) : (financialSettings?.commissione_booking || 15)}%)</span>
                          <span>-{formatCurrency((lordo - tassaSoggiorno) * (source?.toLowerCase() === 'airbnb' ? (financialSettings?.commissione_airbnb || 3) : (financialSettings?.commissione_booking || 15)) / 100)}</span>
                        </div>
                        <div className="border-t pt-1 mt-1 flex justify-between font-semibold text-emerald-700">
                          <span>NETTO DA INCASSARE</span>
                          <span>{formatCurrency(netto)}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Prezzo Lordo (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editForm.prezzo_totale}
                          onChange={(e) => setEditForm({...editForm, prezzo_totale: parseFloat(e.target.value) || ''})}
                          className="mt-1 font-semibold"
                        />
                      </div>
                      <div>
                        <Label>Acconto Ricevuto</Label>
                        <div className="mt-1 h-10 px-3 py-2 rounded-md border bg-green-50 border-green-200 flex items-center">
                          <span className="text-green-700 font-semibold">€{acconto.toFixed(2)}</span>
                        </div>
                      </div>
                      <div>
                        <Label>{isOTA ? 'Saldo Netto Dovuto' : 'Saldo Dovuto'}</Label>
                        <div className={`mt-1 h-10 px-3 py-2 rounded-md border flex items-center font-semibold ${
                          saldoDovuto > 0 
                            ? 'bg-orange-50 border-orange-200 text-orange-700' 
                            : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        }`}>
                          €{saldoDovuto.toFixed(2)}
                          {saldoDovuto <= 0 && <Check className="w-4 h-4 ml-2" />}
                        </div>
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setEditDialogOpen(false);
                            setTimeout(() => openAccontoDialog(editingBooking), 100);
                          }}
                          className="w-full text-emerald-600 border-emerald-600 hover:bg-emerald-50"
                        >
                          <Wallet className="w-4 h-4 mr-2" />
                          Registra Acconto
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Notes Section */}
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#C5A059]" />
                Note
              </h3>
              <Textarea
                value={editForm.note}
                onChange={(e) => setEditForm({...editForm, note: e.target.value})}
                placeholder="Note sulla prenotazione..."
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          {/* Footer */}
          <div className="border-t pt-4 flex justify-between items-center">
            <Button 
              variant="outline" 
              onClick={() => {
                if (window.confirm('Eliminare questa prenotazione?')) {
                  deleteBooking(editingBooking?.id);
                  setEditDialogOpen(false);
                }
              }}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Elimina
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={saveBookingEdit} className="bg-[#C5A059] hover:bg-[#B08D45]">
                <Check className="w-4 h-4 mr-2" />
                Salva Modifiche
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Acconto Dialog */}
      <Dialog open={accontoDialogOpen} onOpenChange={setAccontoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-600" />
              Registra Acconto
            </DialogTitle>
          </DialogHeader>
          
          {selectedBookingAcconto && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                <p className="font-medium">{selectedBookingAcconto.nome_ospite}</p>
                <p className="text-sm text-gray-600">
                  {selectedBookingAcconto.data_arrivo} → {selectedBookingAcconto.data_partenza}
                </p>
                <div className="flex gap-4 text-sm">
                  <span>Totale: <strong>€{selectedBookingAcconto.prezzo_totale}</strong></span>
                  {selectedBookingAcconto.acconto_importo > 0 && (
                    <span className="text-green-600">Già versato: €{selectedBookingAcconto.acconto_importo}</span>
                  )}
                </div>
              </div>

              <form onSubmit={handleAccontoSubmit} className="space-y-4">
                <div>
                  <Label>Importo (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={accontoForm.importo}
                    onChange={(e) => setAccontoForm({...accontoForm, importo: e.target.value})}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label>Metodo *</Label>
                  <select
                    value={accontoForm.metodo}
                    onChange={(e) => setAccontoForm({...accontoForm, metodo: e.target.value})}
                    className="w-full mt-1 border rounded-md px-3 py-2"
                    required
                  >
                    <option value="bonifico">Bonifico</option>
                    <option value="carta">Carta</option>
                    <option value="contanti">Contanti</option>
                    <option value="paypal">PayPal</option>
                  </select>
                </div>
                <div>
                  <Label>Note</Label>
                  <Input
                    value={accontoForm.note}
                    onChange={(e) => setAccontoForm({...accontoForm, note: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setAccontoDialogOpen(false)} className="flex-1">
                    Annulla
                  </Button>
                  <Button type="submit" disabled={savingAcconto} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    {savingAcconto ? 'Salvataggio...' : 'Registra'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Timeline Dialog */}
      <Dialog open={timelineDialogOpen} onOpenChange={setTimelineDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-600" />
              Cronologia Comunicazioni
            </DialogTitle>
          </DialogHeader>
          
          {selectedBookingTimeline && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium">{selectedBookingTimeline.nome_ospite}</p>
                  <p className="text-sm text-gray-600">
                    {selectedBookingTimeline.codice_prenotazione} • {selectedBookingTimeline.unit_nome}
                  </p>
                </div>
              </div>

              <CommunicationTimeline
                bookingId={selectedBookingTimeline.id}
                limit={50}
                compact={false}
                showQuickActions={true}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Expenses Dialog */}
      <Dialog open={expensesDialogOpen} onOpenChange={setExpensesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-[#C5A059]" />
              Spese Dettagliate
            </DialogTitle>
          </DialogHeader>
          
          {loadingExpenses ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#C5A059] border-t-transparent"></div>
            </div>
          ) : selectedBookingExpenses && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Soggiorno</p>
                  <p className="text-xl font-bold">€{selectedBookingExpenses.totale_soggiorno?.toFixed(2)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Servizi</p>
                  <p className="text-xl font-bold text-green-600">€{selectedBookingExpenses.totale_servizi?.toFixed(2)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Totale</p>
                  <p className="text-xl font-bold text-[#C5A059]">€{selectedBookingExpenses.totale_complessivo?.toFixed(2)}</p>
                </div>
              </div>
              
              <Button onClick={() => setExpensesDialogOpen(false)} className="w-full">
                Chiudi
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Change Unit Dialog */}
      <Dialog open={changeUnitDialogOpen} onOpenChange={setChangeUnitDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-blue-600" />
              Cambia Alloggio
            </DialogTitle>
          </DialogHeader>
          
          {selectedBookingForUnitChange && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedBookingForUnitChange.nome_ospite}</p>
                <p className="text-sm text-gray-600">
                  Alloggio attuale: <strong>{selectedBookingForUnitChange.unit_nome}</strong>
                </p>
              </div>
              
              <div>
                <Label className="mb-2 block">Seleziona nuova unità</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableUnits.map(unit => (
                    <label 
                      key={unit.id} 
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
                        selectedNewUnit === unit.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : unit.is_current
                          ? 'border-gray-300 bg-gray-100 opacity-50'
                          : unit.is_available
                          ? 'border-gray-200 hover:border-blue-300'
                          : 'border-orange-200 bg-orange-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="newUnit"
                        value={unit.id}
                        checked={selectedNewUnit === unit.id}
                        onChange={(e) => setSelectedNewUnit(e.target.value)}
                        disabled={unit.is_current}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <span className="font-medium">{unit.nome}</span>
                        {unit.is_current && (
                          <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">Attuale</span>
                        )}
                        {unit.is_available && !unit.is_current && (
                          <span className="ml-2 px-2 py-0.5 bg-green-200 text-green-700 text-xs rounded">Disponibile</span>
                        )}
                        {!unit.is_available && !unit.is_current && (
                          <span className="ml-2 px-2 py-0.5 bg-orange-200 text-orange-700 text-xs rounded">Occupato</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Override checkbox per unità occupate */}
              {selectedNewUnit && availableUnits.find(u => u.id === selectedNewUnit && !u.is_available && !u.is_current) && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={overrideConflict}
                      onChange={(e) => setOverrideConflict(e.target.checked)}
                      className="w-4 h-4 mt-1 text-orange-600"
                    />
                    <div>
                      <span className="font-medium text-orange-800">Forza assegnazione</span>
                      <p className="text-xs text-orange-600 mt-0.5">
                        L'unità selezionata risulta occupata in queste date. 
                        Attiva questa opzione per forzare comunque l'assegnazione.
                      </p>
                    </div>
                  </label>
                </div>
              )}
              
              <div>
                <Label className="mb-2 block">Motivo del cambio</Label>
                <Input
                  value={unitChangeReason}
                  onChange={(e) => setUnitChangeReason(e.target.value)}
                  placeholder="Es: Richiesta cliente, upgrade..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeUnitDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleUnitChange}
              disabled={changingUnit || !selectedNewUnit}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {changingUnit ? 'Salvataggio...' : 'Cambia Alloggio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
